/** Вкладка «Производство» — порт renderProductionMatrix (build-скрипт, стр. 7365-7675).
 * Режимы: ввод плана / контроль (только строки с планом) / объединения (фаза 9).
 * Недельная сетка: инпут (абсолют, канал '' на строке артикула), отметка «произведено»,
 * подсветка правок цветом недели правки, noteParts (литраж | котёл | варки | warning).
 * Канальные строки — раскрытием артикула. Покрытие: прогноз/динамика/нужно/сигнал. */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import {
  buildProductionCsv,
  buildProductionListCsv,
  downloadTextFile,
  parseProductionCsv,
  type ProductionCsvRow,
} from "./csv";
import { int0, monthFullLabel, signed } from "./format";
import { buildView, daysBetween, matchesSearch, type ViewRow } from "./metrics";
import {
  defaultPlanBounds,
  monthsBetween,
  planKey,
  planValueTotal,
  salesPlanChannels,
  yearsOfMonths,
  type PlanMap,
} from "./plans";
import {
  articleBottleMl,
  displayValue,
  productionBatchPlan,
  productionCoverageSignal,
  productionKind,
  productionLiters,
  productionOrderRank,
  productionWarnings,
  productionWeekIndex,
  productionYearWeeks,
  type CoverageSignal,
  type ProductionWeek,
} from "./production";
import { ProductionMergeList } from "./ProductionMergeList";
import { changeColorFor, mergeChangeColors } from "./settings-defaults";
import { useOrdersEdit } from "./useOrdersEdit";

const COVERAGE_CLS: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  under: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  urgent: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  over: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
  check: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400",
};

type Mode = "input" | "control" | "merge";
type CoverageFilter = "all" | "urgent" | "under" | "over" | "check" | "ok";

interface ProdRow {
  view: ViewRow;
  kind: string;
  ml: number;
  rank: number;
  sales3: number[]; // продажи за 3 последних месяца периода
  coverage: CoverageSignal;
  planned: number;
}

interface FlatRow {
  kind: "article" | "channel";
  row: ProdRow;
  channelCode?: string;
  channelLabel?: string;
}

const ROW_H = 52;
const WEEK_W = 118;

export function ProductionTab({ report }: { report: api.OrdersMatrixReport }): React.ReactElement {
  const qc = useQueryClient();
  const canEdit = useOrdersEdit();
  const { user } = useAuth();
  const bounds = defaultPlanBounds(
    report.meta.min_date ?? report.date_from,
    report.meta.max_date ?? report.date_to,
  );
  const [planFrom, setPlanFrom] = useState(bounds.from);
  const [planTo, setPlanTo] = useState(bounds.to);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<Mode>("input");
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>("all");
  const [weekSort, setWeekSort] = useState<{ key: string; dir: "desc" | "asc" } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const planMonths = useMemo(
    () => monthsBetween(planFrom.slice(0, 7), planTo.slice(0, 7)),
    [planFrom, planTo],
  );
  const years = useMemo(() => yearsOfMonths(planMonths), [planMonths]);
  const productionYear = Number(planTo.slice(0, 4));

  // недели периода по годам (порт productionCalendarForPeriod)
  const weeks = useMemo<ProductionWeek[]>(() => {
    const result: ProductionWeek[] = [];
    for (const year of years) {
      for (const w of productionYearWeeks(year)) {
        if (w.to >= planFrom && w.from <= planTo) result.push(w);
      }
    }
    return result;
  }, [years, planFrom, planTo]);
  const monthGroups = useMemo(() => {
    const groups: { month: string; weeks: ProductionWeek[] }[] = [];
    for (const w of weeks) {
      const last = groups[groups.length - 1];
      if (last && last.month === w.month) last.weeks.push(w);
      else groups.push({ month: w.month, weeks: [w] });
    }
    return groups;
  }, [weeks]);

  // факт (кламп к максимуму данных) — кеш общий с PlanTab
  const factTo = report.meta.max_date && planTo > report.meta.max_date ? report.meta.max_date : planTo;
  const factQ = useQuery({
    queryKey: ["orders", "matrix", planFrom, factTo],
    queryFn: () => api.getMatrix({ date_from: planFrom, date_to: factTo }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const attrsQ = useQuery({ queryKey: ["orders", "refs", "attrs"], queryFn: planningApi.listAttrs });
  const channelsQ = useQuery({
    queryKey: ["orders", "refs", "channels"],
    queryFn: planningApi.listChannels,
  });
  const settingsQ = useQuery({
    queryKey: ["orders", "refs", "settings"],
    queryFn: planningApi.listSettings,
  });
  const changeColors = useMemo(
    () =>
      mergeChangeColors(
        (settingsQ.data ?? []).find((s) => s.key === "production_change_colors")?.value,
      ),
    [settingsQ.data],
  );

  const prodQs = useQueries({
    queries: years.map((year) => ({
      queryKey: ["orders", "production", year],
      queryFn: () => planningApi.getProduction(year),
    })),
  });
  const planQs = useQueries({
    queries: years.map((year) => ({
      queryKey: ["orders", "sales-plan-map", year],
      queryFn: async () => {
        const cells = await planningApi.listSalesPlan(year);
        const map: PlanMap = new Map();
        for (const c of cells) map.set(planKey(c.article, c.channel_code, c.month), c.qty);
        return { year, map };
      },
    })),
  });

  const production = useMemo(() => {
    const cells = new Map<string, planningApi.ProductionCell[]>(); // article|week → cells
    const produced = new Set<string>(); // article|week
    const comments = new Map<string, string>(); // article|month|channel → text
    const changes = new Map<string, planningApi.ProductionChange>(); // article|week
    for (const q of prodQs) {
      if (!q.data) continue;
      for (const c of q.data.cells) {
        const key = `${c.article}|${c.week_key}`;
        const list = cells.get(key);
        if (list) list.push(c);
        else cells.set(key, [c]);
      }
      for (const p of q.data.produced) produced.add(`${p.article}|${p.week_key}`);
      for (const c of q.data.comments) {
        comments.set(`${c.article}|${c.month}|${c.channel_code}`, c.text);
      }
      for (const c of q.data.changes) changes.set(`${c.article}|${c.week_key}`, c);
    }
    return { cells, produced, comments, changes };
  }, [prodQs]);

  const planMaps = useMemo(() => {
    const maps = new Map<number, PlanMap>();
    for (const q of planQs) if (q.data) maps.set(q.data.year, q.data.map);
    return maps;
  }, [planQs]);

  const channels = useMemo(() => salesPlanChannels(channelsQ.data ?? []), [channelsQ.data]);
  const attrsMap = useMemo(
    () => new Map((attrsQ.data ?? []).map((a) => [a.article, a])),
    [attrsQ.data],
  );

  const displayFor = (article: string, weekKey: string): number =>
    displayValue(production.cells.get(`${article}|${weekKey}`) ?? []);

  // ── строки ──
  const rows = useMemo<ProdRow[]>(() => {
    if (!factQ.data) return [];
    const { rows: viewRows, ctx } = buildView(factQ.data, "all", "");
    const days = daysBetween(planFrom, factTo);
    const last3 = ctx.months.slice(-3);
    const result: ProdRow[] = [];
    for (const view of viewRows) {
      if (!matchesSearch(view, search)) continue;
      const attrs = attrsMap.get(view.article);
      const kind = productionKind(view.name, attrs);
      const ml = articleBottleMl(view.article, view.name, attrs);
      const scalars = ctx.articleScalars.get(view.article);
      const forecast = planMonths.reduce((s, month) => {
        const map = planMaps.get(Number(month.slice(0, 4)));
        if (!map) return s;
        return s + planValueTotal(map, channels, view.article, Number(month.slice(5, 7)));
      }, 0);
      const planned = weeks.reduce((s, w) => s + displayFor(view.article, w.key), 0);
      const coverage = productionCoverageSignal({
        days,
        recentQty: scalars?.last90 ?? 0,
        last30Qty: scalars?.last30 ?? 0,
        forecast,
        planned,
        stockTotal: view.stockTotal,
      });
      result.push({
        view,
        kind,
        ml,
        rank: productionOrderRank(kind, ml),
        sales3: last3.map((m) => {
          const i = ctx.months.indexOf(m);
          return i >= 0 ? (view.qty[i] ?? 0) : 0;
        }),
        coverage,
        planned,
      });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factQ.data, search, attrsMap, planMaps, channels, weeks, planFrom, factTo, planMonths, production]);

  const filtered = useMemo(() => {
    let list = rows;
    if (coverageFilter !== "all") list = list.filter((r) => r.coverage.status === coverageFilter);
    if (mode === "control") {
      list = list.filter((r) => weeks.some((w) => displayFor(r.view.article, w.key) > 0));
    }
    if (weekSort) {
      const dir = weekSort.dir === "asc" ? 1 : -1;
      list = [...list]
        .filter((r) => displayFor(r.view.article, weekSort.key) > 0)
        .sort((a, b) => dir * (displayFor(a.view.article, weekSort.key) - displayFor(b.view.article, weekSort.key)));
    } else {
      list = [...list].sort(
        (a, b) =>
          a.rank - b.rank ||
          b.sales3.reduce((s, v) => s + v, 0) - a.sales3.reduce((s, v) => s + v, 0) ||
          a.view.article.localeCompare(b.view.article, "ru"),
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, coverageFilter, mode, weekSort, weeks, production]);

  const flat = useMemo<FlatRow[]>(() => {
    const list: FlatRow[] = [];
    for (const row of filtered) {
      list.push({ kind: "article", row });
      if (expanded.has(row.view.article)) {
        for (const ch of channels) {
          list.push({ kind: "channel", row, channelCode: ch.code, channelLabel: ch.label });
        }
      }
    }
    return list;
  }, [filtered, expanded, channels]);

  // ── мутации ──
  const invalidateProd = () => qc.invalidateQueries({ queryKey: ["orders", "production"] });
  const cellMut = useMutation({
    mutationFn: planningApi.putProductionCell,
    onSuccess: invalidateProd,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить план")),
  });
  const producedMut = useMutation({
    mutationFn: planningApi.setProduced,
    onSuccess: invalidateProd,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось отметить производство")),
  });
  const commentMut = useMutation({
    mutationFn: planningApi.putProductionComment,
    onSuccess: invalidateProd,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить комментарий")),
  });
  const changeClearMut = useMutation({
    mutationFn: planningApi.clearProductionChange,
    onSuccess: invalidateProd,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось снять подсветку")),
  });
  const bulkMut = useMutation({
    mutationFn: (entries: { article: string; weeks: Record<string, number>; comments: Record<string, string> }[]) =>
      planningApi.bulkProduction(productionYear, entries),
    onSuccess: (r) => {
      invalidateProd();
      toast.success(`Импортировано значений: ${r.written}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Импорт не удался")),
  });
  const clearMut = useMutation({
    mutationFn: () => planningApi.clearProduction(productionYear),
    onSuccess: () => {
      invalidateProd();
      toast.success(`Производство ${productionYear} очищено`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось очистить")),
  });

  const gridTemplate = useMemo(() => {
    const fixed = "280px 90px 70px 80px 80px 80px 84px 84px 84px 84px 84px 84px 110px";
    const monthCols = monthGroups
      .flatMap((g) => [...g.weeks.map(() => `${WEEK_W}px`), "84px", "150px"])
      .join(" ");
    return `${fixed} ${monthCols} 96px`;
  }, [monthGroups]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: flat.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });

  const fileRef = useRef<HTMLInputElement>(null);
  const loading = factQ.isLoading || prodQs.some((q) => q.isLoading) || planQs.some((q) => q.isLoading);

  if (loading) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" /> Загружаю производство, план и факт…
      </div>
    );
  }
  if (factQ.isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
        Не удалось получить данные: {extractErrorMessage(factQ.error, "ошибка запроса")}
      </div>
    );
  }

  const clickWeekSort = (key: string): void => {
    if (!weekSort || weekSort.key !== key) setWeekSort({ key, dir: "desc" });
    else if (weekSort.dir === "desc") setWeekSort({ key, dir: "asc" });
    else setWeekSort(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Период:</span>
        <input
          type="date"
          value={planFrom}
          onChange={(e) => e.target.value && setPlanFrom(e.target.value)}
          className="h-8 rounded border bg-background px-2 text-xs"
        />
        <span className="text-muted-foreground">—</span>
        <input
          type="date"
          value={planTo}
          onChange={(e) => e.target.value && setPlanTo(e.target.value)}
          className="h-8 rounded border bg-background px-2 text-xs"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск"
          className="h-8 w-48 rounded border bg-background px-2 text-xs"
        />
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
          className="h-8 rounded border bg-background px-2 text-xs"
        >
          <option value="input">Ввод плана</option>
          <option value="control">Контроль (есть план)</option>
          <option value="merge">Объединения варок</option>
        </select>
        <select
          value={coverageFilter}
          onChange={(e) => setCoverageFilter(e.target.value as CoverageFilter)}
          className="h-8 rounded border bg-background px-2 text-xs"
        >
          <option value="all">Покрытие: все</option>
          <option value="urgent">Срочно</option>
          <option value="under">Недоплан</option>
          <option value="over">Переплан</option>
          <option value="check">Прогноз проверить</option>
          <option value="ok">OK</option>
        </select>
        <span className="text-xs text-muted-foreground">строк: {filtered.length}</span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const csvRows: ProductionCsvRow[] = filtered.map((r) => ({
                article: r.view.article,
                name: r.view.name,
                group: attrsMap.get(r.view.article)?.product_type ?? "",
                characteristic: attrsMap.get(r.view.article)?.characteristic ?? "",
                volume_ml: attrsMap.get(r.view.article)?.volume_ml ?? null,
                weeks: Object.fromEntries(weeks.map((w) => [w.key, displayFor(r.view.article, w.key)])),
                comments: Object.fromEntries(
                  planMonths.map((m) => [m, production.comments.get(`${r.view.article}|${m}|`) ?? ""]),
                ),
              }));
              downloadTextFile(
                `production-plan-template-${productionYear}.csv`,
                buildProductionCsv(weeks.map((w) => ({ key: w.key, month: w.month })), csvRows),
              );
            }}
          >
            Шаблон
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const listRows: Parameters<typeof buildProductionListCsv>[0] = [];
              for (const r of filtered) {
                for (const w of weeks) {
                  const cells = production.cells.get(`${r.view.article}|${w.key}`) ?? [];
                  const channelCells = cells.filter((c) => c.channel_code !== "" && c.qty > 0);
                  const producedMark = production.produced.has(`${r.view.article}|${w.key}`);
                  if (channelCells.length) {
                    for (const c of channelCells) {
                      listRows.push({
                        article: r.view.article, name: r.view.name,
                        channel_label: channels.find((ch) => ch.code === c.channel_code)?.label ?? c.channel_code,
                        qty: c.qty, week_label: `Н${w.index}`, week_range: w.range, produced: producedMark,
                      });
                    }
                  } else {
                    const qty = displayFor(r.view.article, w.key);
                    if (qty > 0) {
                      listRows.push({
                        article: r.view.article, name: r.view.name, channel_label: "Без канала",
                        qty, week_label: `Н${w.index}`, week_range: w.range, produced: producedMark,
                      });
                    }
                  }
                }
              }
              if (!listRows.length) {
                toast.error("Нет плана за период");
                return;
              }
              listRows.sort(
                (a, b) =>
                  productionWeekIndex(a.week_label.replace("Н", "0000-W")) -
                    productionWeekIndex(b.week_label.replace("Н", "0000-W")) ||
                  a.article.localeCompare(b.article, "ru"),
              );
              downloadTextFile(`production-sales-plan-list-${productionYear}.csv`, buildProductionListCsv(listRows));
            }}
          >
            Экспорт
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                Импорт
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const entries = parseProductionCsv(await file.text());
                  if (!entries.length) {
                    toast.error("В файле нет строк производства");
                    return;
                  }
                  bulkMut.mutate(entries);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 dark:text-red-400"
                onClick={() => {
                  if (window.confirm(`Очистить план производства ${productionYear} года?`)) {
                    clearMut.mutate();
                  }
                }}
              >
                Очистить
              </Button>
            </>
          )}
        </div>
      </div>

      {mode === "merge" ? (
        <ProductionMergeList
          articles={filtered.map((r) => ({
            article: r.view.article,
            name: r.view.name,
            ml: r.ml,
            attrs: attrsMap.get(r.view.article),
          }))}
          weeks={weeks}
          production={{
            cells: production.cells,
            produced: production.produced,
            confirmations: new Map(
              prodQs.flatMap((q) =>
                (q.data?.confirmations ?? []).map(
                  (c) => [`${c.article}|${c.week_key}`, c] as const,
                ),
              ),
            ),
          }}
          year={productionYear}
          canEdit={canEdit}
        />
      ) : (
      <div ref={parentRef} className="max-h-[calc(100vh-260px)] overflow-auto rounded-md border">
        <div className="min-w-max">
          <div className="sticky top-0 z-20 bg-background text-[11px] font-medium text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
            {/* месяцы */}
            <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="sticky left-0 z-10 bg-background" style={{ gridColumn: "span 13" }} />
              {monthGroups.map((g) => (
                <div
                  key={g.month}
                  className="border-l px-2 py-0.5 text-center font-semibold"
                  style={{ gridColumn: `span ${g.weeks.length + 2}` }}
                >
                  {monthFullLabel(g.month)}
                </div>
              ))}
              <div />
            </div>
            {/* колонки */}
            <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="sticky left-0 z-10 bg-background px-2 py-1">Артикул / канал</div>
              <div className="border-l px-2 py-1">Тип</div>
              <div className="border-l px-2 py-1 text-right">Мл</div>
              {["M-2", "M-1", "M", "3 мес."].map((l) => (
                <div key={l} className="border-l px-2 py-1 text-right">Прод. {l}</div>
              ))}
              <div className="border-l px-2 py-1 text-right">Остаток</div>
              <div className="border-l px-2 py-1 text-right">Прогноз</div>
              <div className="border-l px-2 py-1 text-right">Динамика</div>
              <div className="border-l px-2 py-1 text-right">Нужно</div>
              <div className="border-l px-2 py-1 text-right">Покрытие</div>
              <div className="border-l px-2 py-1">Сигнал</div>
              {monthGroups.flatMap((g) => [
                ...g.weeks.map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => clickWeekSort(w.key)}
                    className={cn(
                      "border-l px-1.5 py-1 text-right hover:text-foreground",
                      weekSort?.key === w.key && "text-foreground underline",
                    )}
                    title={`${w.range} · клик: сортировка/фильтр по неделе`}
                  >
                    Н{w.index}
                    <div className="text-[9px] font-normal">{w.range}</div>
                  </button>
                )),
                <div key={`${g.month}-t`} className="border-l px-2 py-1 text-right font-semibold">Итого</div>,
                <div key={`${g.month}-c`} className="border-l px-2 py-1">Комментарий</div>,
              ])}
              <div className="border-l px-2 py-1 text-right font-semibold">Период</div>
            </div>
          </div>

          <div style={{ height: virt.getTotalSize(), position: "relative" }}>
            {virt.getVirtualItems().map((vi) => {
              const item = flat[vi.index];
              if (!item) return null;
              return (
                <div
                  key={`${item.kind}:${item.row.view.article}:${item.channelCode ?? ""}`}
                  className="absolute left-0 w-full border-b"
                  style={{ top: vi.start, height: ROW_H }}
                >
                  <ProductionRow
                    item={item}
                    weeks={weeks}
                    monthGroups={monthGroups}
                    production={production}
                    displayFor={displayFor}
                    changeColors={changeColors}
                    canEdit={canEdit}
                    isAdmin={!!user?.is_admin}
                    gridTemplate={gridTemplate}
                    attrs={attrsMap.get(item.row.view.article)}
                    expanded={expanded.has(item.row.view.article)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.row.view.article)) next.delete(item.row.view.article);
                        else next.add(item.row.view.article);
                        return next;
                      })
                    }
                    onCell={(weekKey, channel, qty) =>
                      cellMut.mutate({
                        year: productionYearForWeekKey(weekKey),
                        article: item.row.view.article,
                        week_key: weekKey,
                        channel_code: channel,
                        qty,
                      })
                    }
                    onProduced={(weekKey, produced) =>
                      producedMut.mutate({
                        year: productionYearForWeekKey(weekKey),
                        article: item.row.view.article,
                        week_key: weekKey,
                        produced,
                      })
                    }
                    onComment={(month, channel, text) =>
                      commentMut.mutate({
                        year: Number(month.slice(0, 4)),
                        article: item.row.view.article,
                        month,
                        channel_code: channel,
                        text,
                      })
                    }
                    onClearChange={(weekKey) =>
                      changeClearMut.mutate({
                        year: productionYearForWeekKey(weekKey),
                        article: item.row.view.article,
                        week_key: weekKey,
                      })
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function productionYearForWeekKey(weekKey: string): number {
  return Number(weekKey.slice(0, 4));
}

function ProductionRow({
  item,
  weeks,
  monthGroups,
  production,
  displayFor,
  changeColors,
  canEdit,
  isAdmin,
  gridTemplate,
  attrs,
  expanded,
  onToggle,
  onCell,
  onProduced,
  onComment,
  onClearChange,
}: {
  item: FlatRow;
  weeks: ProductionWeek[];
  monthGroups: { month: string; weeks: ProductionWeek[] }[];
  production: {
    cells: Map<string, planningApi.ProductionCell[]>;
    produced: Set<string>;
    comments: Map<string, string>;
    changes: Map<string, planningApi.ProductionChange>;
  };
  displayFor: (article: string, weekKey: string) => number;
  changeColors: string[];
  canEdit: boolean;
  isAdmin: boolean;
  gridTemplate: string;
  attrs: planningApi.ArticleAttrs | undefined;
  expanded: boolean;
  onToggle: () => void;
  onCell: (weekKey: string, channel: string, qty: number) => void;
  onProduced: (weekKey: string, produced: boolean) => void;
  onComment: (month: string, channel: string, text: string) => void;
  onClearChange: (weekKey: string) => void;
}): React.ReactElement {
  const { row, channelCode, channelLabel } = item;
  const isChannel = item.kind === "channel";
  const article = row.view.article;
  const num = "flex items-center justify-end border-l px-2 tabular-nums";

  const channelQty = (weekKey: string): number => {
    const cells = production.cells.get(`${article}|${weekKey}`) ?? [];
    return cells.find((c) => c.channel_code === channelCode)?.qty ?? 0;
  };

  return (
    <div
      className={cn("grid h-full items-stretch text-xs", isChannel && "bg-muted/30")}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      <div className="sticky left-0 z-10 flex items-center gap-1 overflow-hidden bg-background px-2">
        {!isChannel ? (
          <>
            <button type="button" onClick={onToggle} className="shrink-0 text-muted-foreground hover:text-foreground">
              {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            </button>
            <div className="min-w-0">
              <div className="truncate font-medium">{article}</div>
              <div className="truncate text-[10px] text-muted-foreground" title={row.view.name}>
                {row.view.name}
              </div>
            </div>
          </>
        ) : (
          <span className="ml-6 truncate text-muted-foreground">{channelLabel}</span>
        )}
      </div>
      <div className="flex items-center truncate border-l px-2 text-muted-foreground">
        {!isChannel && row.kind}
      </div>
      <div className={num}>{!isChannel && (row.ml || "—")}</div>
      {row.sales3.map((v, i) => (
        <div key={i} className={cn(num, "text-muted-foreground")}>{!isChannel && int0(v)}</div>
      ))}
      <div className={cn(num, "text-muted-foreground")}>
        {!isChannel && int0(row.sales3.reduce((s, v) => s + v, 0))}
      </div>
      <div className={num}>{!isChannel && int0(row.view.stockTotal)}</div>
      <div className={num}>{!isChannel && int0(row.coverage.forecast)}</div>
      <div className={num}>{!isChannel && int0(row.coverage.dynamic)}</div>
      <div className={num}>{!isChannel && int0(row.coverage.need)}</div>
      <div className={num}>{!isChannel && int0(row.coverage.coverage)}</div>
      <div className="flex items-center border-l px-1.5">
        {!isChannel && (
          <span
            className={cn("truncate rounded px-1.5 py-0.5 text-[10px] font-medium", COVERAGE_CLS[row.coverage.status])}
            title={`${row.coverage.note}\nпрогноз ${int0(row.coverage.forecast)} · динамика ${int0(row.coverage.dynamic)} · покрытие ${int0(row.coverage.coverage)} · отклонение ${signed(row.coverage.deviation)} (${row.coverage.deviationPct}%)`}
          >
            {row.coverage.label}
            <small className="ml-1">{signed(row.coverage.deviation)}</small>
          </span>
        )}
      </div>
      {monthGroups.flatMap((g) => {
        const monthCells = g.weeks.map((w) => {
          const key = `${article}|${w.key}`;
          const qty = isChannel ? channelQty(w.key) : displayFor(article, w.key);
          const producedMark = production.produced.has(key);
          const change = !isChannel ? production.changes.get(key) : undefined;
          const liters = productionLiters(row.ml, qty);
          const kettle = qty > 0 ? productionBatchPlan(liters) : null;
          const warnings = !isChannel && qty > 0
            ? productionWarnings(row.view.name, attrs, row.ml, qty)
            : [];
          const note = qty > 0 && !isChannel
            ? [liters ? `${liters} л` : "", kettle?.name !== "-" ? kettle?.name : "", kettle && kettle.batches > 1 ? `${kettle.batches} вар.` : "", warnings[0] ?? ""]
                .filter(Boolean)
                .join(" | ")
            : "";
          return (
            <div
              key={w.key}
              className={cn(
                "border-l px-1 py-0.5",
                producedMark && "bg-emerald-50 opacity-60 dark:bg-emerald-950/40",
              )}
              style={
                change
                  ? { boxShadow: `inset 0 3px 0 0 ${changeColorFor(change.edit_week, changeColors)}` }
                  : undefined
              }
              title={
                change
                  ? `правка Н${change.edit_week}: ${change.old_value} → ${change.new_value} (${change.username})`
                  : note
              }
            >
              <div className="flex items-center gap-1">
                {canEdit ? (
                  <input
                    key={`${w.key}:${qty}`}
                    type="number"
                    min={0}
                    defaultValue={qty || ""}
                    className="h-6 w-14 rounded border bg-background px-1 text-right text-[11px] focus:border-primary focus:outline-none"
                    onBlur={(e) => {
                      const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                      if (v !== qty) onCell(w.key, isChannel ? channelCode! : "", v);
                    }}
                  />
                ) : (
                  <span className="w-14 text-right tabular-nums">{qty || "—"}</span>
                )}
                {!isChannel && (
                  <input
                    type="checkbox"
                    checked={producedMark}
                    disabled={!canEdit}
                    title="Произведено"
                    onChange={(e) => onProduced(w.key, e.target.checked)}
                  />
                )}
                {change && isAdmin && (
                  <button
                    type="button"
                    className="text-[9px] text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                    title="Убрать подсветку правки (админ)"
                    onClick={() => {
                      if (window.confirm("Убрать подсветку правки?")) onClearChange(w.key);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              {!isChannel && note && (
                <div className="truncate text-[9px] text-muted-foreground" title={note}>
                  {note}
                </div>
              )}
            </div>
          );
        });
        const monthTotal = g.weeks.reduce(
          (s, w) => s + (isChannel ? channelQty(w.key) : displayFor(article, w.key)),
          0,
        );
        const commentKey = `${article}|${g.month}|${isChannel ? channelCode : ""}`;
        const commentText = production.comments.get(commentKey) ?? "";
        return [
          ...monthCells,
          <div key={`${g.month}-t`} className={cn(num, "bg-muted/40 font-medium")}>
            {monthTotal ? int0(monthTotal) : ""}
          </div>,
          <div key={`${g.month}-c`} className="flex items-center border-l px-1">
            <input
              key={`${commentKey}:${commentText}`}
              defaultValue={commentText}
              disabled={!canEdit}
              placeholder="Комментарий"
              className="h-6 w-full rounded border-0 bg-transparent px-1 text-[10px] focus:border focus:border-primary focus:bg-background focus:outline-none"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== commentText) onComment(g.month, isChannel ? channelCode! : "", v);
              }}
            />
          </div>,
        ];
      })}
      <div className={cn(num, "font-semibold")}>
        {int0(
          weeks.reduce((s, w) => s + (isChannel ? channelQty(w.key) : displayFor(article, w.key)), 0),
        )}
      </div>
    </div>
  );
}
